import React, { useState, useEffect, useRef } from 'react';
import { useUser } from '@clerk/clerk-react';
import PickCard from './components/PickCard';
// import SportsbookCarousel from './components/SportsbookCarousel';
import FilterBar from './components/FilterBar';
import ParlayBuilder from './components/ParlayBuilder';
import PlayerStatsModal from './components/PlayerStatsModal';
import LandingPage from './components/LandingPage';
import LoginScreen from './components/LoginScreen';
import SubscriptionCheckout from './components/SubscriptionCheckout';
import { useSubscription } from './hooks/useSubscription';
import './App.css';

const DEFAULT_FILTERS = {
  l5: 0,
  l10: 0,
  l20: 0,
  h2h: 0,
  season: 0,
  l50: 0,
  noVigOver: 0,
  noVigUnder: 0
};

const LEAGUE_DISPLAY = {
  nba: 'NBA',
  nfl: 'NFL'
};

const LEAGUE_LOGOS = {
  nba: '/logos/nba-logo-transparent.png',
  nfl: '/logos/nfllogo.png'
};

function App({ clerkUnavailable = false }) {
  if (clerkUnavailable) {
    return <AppContent clerkUnavailable loaded signedIn={false} user={null} />;
  }
  return <AppWithClerk />;
}

function AppWithClerk() {
  const { isLoaded, isSignedIn, signOut, user } = useUser();
  return <AppContent clerkUnavailable={false} loaded={isLoaded} signedIn={isSignedIn} signOut={signOut} user={user} />;
}

function AppContent({ clerkUnavailable, loaded, signedIn, signOut, user }) {
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef(null);
  const [showCheckout, setShowCheckout] = useState(false);
  const subscription = useSubscription();

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserMenu]);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterMode, setFilterMode] = useState('hit'); // 'hit' or 'miss'
  const [parlayPicks, setParlayPicks] = useState([]);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [selectedGame, setSelectedGame] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedLeague, setSelectedLeague] = useState('nba');
  const isLandingRoute = typeof window !== 'undefined' && window.location.pathname === '/land';
  
  // Authentication is required
  const effectiveSignedIn = signedIn;
  const effectiveLoaded = loaded;

  useEffect(() => {
    if (!effectiveLoaded) {
      return;
    }
    if (effectiveSignedIn) {
      setFilters({
        ...DEFAULT_FILTERS,
        ...(selectedLeague === 'nfl' ? { l20: 0, l50: 0 } : {})
      });
      setSearchQuery('');
      setParlayPicks([]);
      loadGamesData(selectedLeague);
    } else {
      setGames([]);
      setError(null);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveLoaded, effectiveSignedIn, selectedLeague]);

  const handleLandingAuthenticate = () => {
    if (!clerkUnavailable) {
      setLoading(true);
    }
  };

  const normalizePick = (pick, league) => {
    const normalized = { ...pick };

    normalized.league = league;
    normalized.player_id = pick.player_id ?? null;
    normalized.espn_id = pick.espn_id ?? null;
    
    // Preserve defensive analysis data
    normalized.defensive_analysis = pick.defensive_analysis ?? null;
    normalized.opponent_points_rank = pick.opponent_points_rank ?? pick.defensive_analysis?.opponent_points_rank ?? null;
    normalized.defensive_rank = pick.defensive_rank ?? null;

    // Map different field naming conventions
    const last5 = pick.last_5_game_values ?? pick.recent_5_game_values ?? [];
    const last10 = pick.last_10_game_values ?? pick.recent_10_game_values ?? [];
    const last20 = pick.last_20_game_values ?? pick.recent_20_game_values ?? [];
    const last50 = pick.last_50_game_values ?? pick.recent_50_game_values ?? [];

    normalized.last_5_game_values = last5;
    normalized.last_10_game_values = last10;
    normalized.last_20_game_values = last20;
    normalized.last_50_game_values = last50;

    normalized.hit_rate_last_5 = pick.hit_rate_last_5 ?? null;
    normalized.hit_rate_last_10 = pick.hit_rate_last_10 ?? null;
    normalized.hit_rate_last_20 = pick.hit_rate_last_20 ?? null;
    normalized.hit_rate_last_50 = pick.hit_rate_last_50 ?? null;
    normalized.hit_rate_overall = pick.hit_rate_overall ?? null;
    normalized.hit_rate_last_season = pick.hit_rate_last_season ?? null;

    // Use overall hit rate if H2H not available
    normalized.hit_rate_overall = pick.hit_rate_overall ?? pick.hit_rate_vs_team ?? null;
    normalized.hit_rate_vs_team = pick.hit_rate_vs_team ?? normalized.hit_rate_overall ?? null;

    normalized.total_last_5 = pick.total_last_5 ?? last5.length ?? 0;
    normalized.total_last_10 = pick.total_last_10 ?? last10.length ?? 0;
    normalized.total_last_20 = pick.total_last_20 ?? last20.length ?? 0;
    normalized.total_last_50 = pick.total_last_50 ?? last50.length ?? 0;

    normalized.overs_last_5 = pick.overs_last_5 ?? null;
    normalized.overs_last_10 = pick.overs_last_10 ?? null;
    normalized.overs_last_20 = pick.overs_last_20 ?? null;
    normalized.overs_last_50 = pick.overs_last_50 ?? null;

    normalized.individual_game_values = pick.individual_game_values ?? [];
    normalized.overall_game_values = pick.overall_game_values ?? [];

    return normalized;
  };

  const loadGamesData = async (league = selectedLeague) => {
    try {
      setLoading(true);
      setError(null);

      // Load the master picks file
      try {
        const response = await fetch(`/${league}_picks_master.json`);
        if (response.ok) {
          const masterData = await response.json();
          console.log(`Loaded ${league.toUpperCase()} master picks data:`, masterData);
          
          // Convert all picks to game format for display
          const allPicks = masterData.all_picks || [];
          console.log(`Found ${allPicks.length} total picks across ${masterData.total_games} games`);
          
          // Group picks by game
          const gameGroups = {};
          allPicks.forEach(pick => {
            const gameName = pick.game;
            if (!gameGroups[gameName]) {
              gameGroups[gameName] = {
                timestamp: masterData.timestamp,
                game: gameName,
                league,
                ai_picks: []
              };
            }
            gameGroups[gameName].ai_picks.push(normalizePick(pick, league));
          });
          
          // Convert to array
          const gamesData = Object.values(gameGroups);
          console.log(`Grouped into ${gamesData.length} games`);
          setGames(gamesData);
          return;
        } else {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
      } catch (error) {
        console.error('Failed to load master picks file:', error);
        if (error instanceof SyntaxError || (error.message && error.message.includes('Unexpected token'))) {
          setError(`Data not processed yet for ${LEAGUE_DISPLAY[league]}. Please check again later.`);
        } else {
          setError(`Failed to load ${LEAGUE_DISPLAY[league]} picks data: ${error.message}`);
        }
        return;
      }
    } catch (err) {
      setError(`Failed to load ${LEAGUE_DISPLAY[league]} games data. Please make sure the JSON files are in the public folder.`);
      console.error(`Error loading ${league} games:`, err);
    } finally {
      setLoading(false);
    }
  };



  if (loading) {
    return (
      <div className="container">
        <div className="loading">
          <h2>Loading {LEAGUE_DISPLAY[selectedLeague]} Picks...</h2>
          <p>Fetching the latest AI recommendations</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="App">
        {/* <SportsbookCarousel /> */}
        <div className="container">
          <header className="header">
            <div className="header-content">
              <div className="header-left">
                <img src="/logos/longLogo.png" alt="LinePicks" className="long-logo" />
              </div>
              <div className="header-right">
                <div className="date-display">
                  {new Date().toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </div>
                {effectiveLoaded && effectiveSignedIn && (
                  <button
                    type="button"
                    className="logout-button"
                    onClick={async () => {
                      try {
                        if (signOut) {
                          await signOut();
                        }
                        // Redirect to landing page after logout
                        if (typeof window !== 'undefined') {
                          window.location.href = '/land';
                        }
                      } catch (error) {
                        console.error('Error signing out:', error);
                        // Still try to redirect even if there's an error
                        if (typeof window !== 'undefined') {
                          window.location.href = '/land';
                        }
                      }
                    }}
                  >
                    Log Out
                  </button>
                )}
              </div>
            </div>
          </header>

          <div className="league-selector">
            {Object.entries(LEAGUE_DISPLAY).map(([league, label]) => (
              <button
                key={league}
                type="button"
                className={`league-button ${selectedLeague === league ? 'active' : ''}`}
                onClick={() => {
                  if (selectedLeague !== league) {
                    setSelectedLeague(league);
                  } else {
                    loadGamesData(league);
                  }
                }}
              >
                <img src={LEAGUE_LOGOS[league]} alt={`${label} logo`} className="league-logo" />
                <span className="league-label">{label}</span>
              </button>
            ))}
          </div>

          <div className="error">
            <h2>❌ Error</h2>
            <p>{error}</p>
            <button onClick={() => loadGamesData(selectedLeague)} style={{
              marginTop: '15px',
              padding: '10px 20px',
              background: '#ff751f',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '1rem'
            }}>
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Filter picks based on filters and search
  const filterPicks = () => {
    const allPicks = [];
    games.forEach(game => {
      game.ai_picks?.forEach(pick => {
        allPicks.push({ pick, game });
      });
    });

    return allPicks.filter(({ pick }) => {
      // Search filter
      if (searchQuery.trim() && !pick.player.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }

      // Filter by hit rates
      const l5 = pick.hit_rate_last_5 !== null && pick.hit_rate_last_5 !== undefined ? parseFloat(pick.hit_rate_last_5) : null;
      const l10 = pick.hit_rate_last_10 !== null && pick.hit_rate_last_10 !== undefined ? parseFloat(pick.hit_rate_last_10) : null;
      const l20 = pick.hit_rate_last_20 !== null && pick.hit_rate_last_20 !== undefined ? parseFloat(pick.hit_rate_last_20) : null;
      const h2h = pick.hit_rate_vs_team !== null && pick.hit_rate_vs_team !== undefined ? parseFloat(pick.hit_rate_vs_team) : null;
      const season = pick.hit_rate_last_season !== null && pick.hit_rate_last_season !== undefined ? parseFloat(pick.hit_rate_last_season) : null;
      const l50 = pick.hit_rate_last_50 !== null && pick.hit_rate_last_50 !== undefined ? parseFloat(pick.hit_rate_last_50) : null;

      // Check if pick meets all filter criteria based on mode
      if (filterMode === 'hit') {
        // Hit mode: show players that hit OVER X% of the time
        if (filters.l5 > 0 && (l5 === null || l5 < filters.l5)) return false;
        if (filters.l10 > 0 && (l10 === null || l10 < filters.l10)) return false;
        if (filters.l20 > 0 && (l20 === null || l20 < filters.l20)) return false;
        if (filters.h2h > 0 && (h2h === null || h2h < filters.h2h)) return false;
        if (filters.season > 0 && (season === null || season < filters.season)) return false;
        if (filters.l50 > 0 && (l50 === null || l50 < filters.l50)) return false;
      } else {
        // Miss mode: show players that missed (under) X% of the time
        // If filter is 90%, we want players with hit rate <= 10% (miss rate >= 90%)
        if (filters.l5 > 0 && (l5 === null || l5 > (100 - filters.l5))) return false;
        if (filters.l10 > 0 && (l10 === null || l10 > (100 - filters.l10))) return false;
        if (filters.l20 > 0 && (l20 === null || l20 > (100 - filters.l20))) return false;
        if (filters.h2h > 0 && (h2h === null || h2h > (100 - filters.h2h))) return false;
        if (filters.season > 0 && (season === null || season > (100 - filters.season))) return false;
        if (filters.l50 > 0 && (l50 === null || l50 > (100 - filters.l50))) return false;
      }

      // Filter by no-vig odds
      const overNoVig = pick.over_prob_no_vig !== null && pick.over_prob_no_vig !== undefined ? parseFloat(pick.over_prob_no_vig) : null;
      const underNoVig = pick.under_prob_no_vig !== null && pick.under_prob_no_vig !== undefined ? parseFloat(pick.under_prob_no_vig) : null;
      
      if (filters.noVigOver > 0 && (overNoVig === null || overNoVig < filters.noVigOver)) return false;
      if (filters.noVigUnder > 0 && (underNoVig === null || underNoVig < filters.noVigUnder)) return false;

      return true;
    });
  };

  const filteredPicks = filterPicks();

  // Show login screen if not authenticated
  if (isLandingRoute) {
    return (
      <LandingPage
        onAuthenticate={handleLandingAuthenticate}
        isClerkReady={!clerkUnavailable && effectiveLoaded}
      />
    );
  }

  if (!effectiveLoaded) {
    return (
      <div className="container">
        <div className="loading">
          <h2>Loading LinePicks…</h2>
          <p>Initializing secure session</p>
        </div>
      </div>
    );
  }

  if (!effectiveSignedIn) {
    return <LoginScreen onLogin={() => setLoading(true)} clerkUnavailable={clerkUnavailable} />;
  }

  // Check subscription status - show checkout if not subscribed
  if (effectiveSignedIn && !subscription.isLoading) {
    if (!subscription.isActive && !showCheckout) {
      return (
        <div className="App">
          <div className="container">
            <div className="subscription-required">
              <div className="subscription-required-content">
                <img src="/logos/longLogo.png" alt="LinePicks" className="subscription-logo" />
                <h2>Subscription Required</h2>
                <p>You need an active subscription to access LinePicks premium features.</p>
                <button
                  type="button"
                  className="cta-primary"
                  onClick={() => setShowCheckout(true)}
                  style={{
                    marginTop: '24px',
                    padding: '14px 32px',
                    fontSize: '1rem',
                  }}
                >
                  Subscribe Now
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (showCheckout) {
      return (
        <SubscriptionCheckout
          onSuccess={() => {
            setShowCheckout(false);
            // Refresh subscription status
            window.location.reload();
          }}
          onCancel={() => setShowCheckout(false)}
        />
      );
    }
  }

  // Show loading while checking subscription
  if (effectiveSignedIn && subscription.isLoading) {
    return (
      <div className="container">
        <div className="loading">
          <h2>Verifying Subscription...</h2>
          <p>Please wait while we check your access</p>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      {/* <SportsbookCarousel /> */}
      <div className="container">
        <header className="header">
          <div className="header-content">
            <div className="header-left">
              <img src="/logos/longLogo.png" alt="LinePicks" className="long-logo" />
            </div>
            <div className="header-center">
              <div className="date-display">
                {new Date().toLocaleDateString('en-US', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </div>
            </div>
            <div className="header-right">
              {effectiveLoaded && effectiveSignedIn && (
                <div className="user-menu-container" ref={userMenuRef}>
                  <button
                    type="button"
                    className="user-profile-button"
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    aria-label="User menu"
                  >
                    {user && user.imageUrl ? (
                      <img 
                        src={user.imageUrl} 
                        alt={user.fullName || 'User'} 
                        className="user-profile-image"
                        onError={(e) => {
                          e.target.src = '/default-avatar.png';
                        }}
                      />
                    ) : (
                      <div className="user-profile-image user-profile-placeholder">
                        {user && user.fullName ? user.fullName.charAt(0).toUpperCase() : 'U'}
                      </div>
                    )}
                  </button>
                  {showUserMenu && (
                    <div className="user-menu-dropdown">
                      <button
                        type="button"
                        className="user-menu-item"
                        onClick={async () => {
                          setShowUserMenu(false);
                          try {
                            if (signOut) {
                              await signOut();
                            }
                            // Redirect to landing page after logout
                            if (typeof window !== 'undefined') {
                              window.location.href = '/land';
                            }
                          } catch (error) {
                            console.error('Error signing out:', error);
                            // Still try to redirect even if there's an error
                            if (typeof window !== 'undefined') {
                              window.location.href = '/land';
                            }
                          }
                        }}
                      >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M6 14H3.33333C2.97971 14 2.64057 13.8595 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667V3.33333C2 2.97971 2.14048 2.64057 2.39052 2.39052C2.64057 2.14048 2.97971 2 3.33333 2H6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M10.6667 11.3333L14 8L10.6667 4.66667" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M14 8H6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Log Out
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </header>

        <div className="league-selector">
          {Object.entries(LEAGUE_DISPLAY).map(([league, label]) => (
            <button
              key={league}
              type="button"
              className={`league-button ${selectedLeague === league ? 'active' : ''}`}
              onClick={() => {
                if (selectedLeague !== league) {
                  setSelectedLeague(league);
                }
              }}
            >
              <img src={LEAGUE_LOGOS[league]} alt={`${label} logo`} className="league-logo" />
              <span className="league-label">{label}</span>
            </button>
          ))}
        </div>

        {parlayPicks.length > 0 && (
          <ParlayBuilder 
            parlayPicks={parlayPicks}
            onRemovePick={(index) => {
              setParlayPicks(parlayPicks.filter((_, i) => i !== index));
            }}
            onUpdatePick={(index, updates) => {
              const updated = [...parlayPicks];
              updated[index] = { ...updated[index], ...updates };
              setParlayPicks(updated);
            }}
            onClearParlay={() => setParlayPicks([])}
          />
        )}

        <FilterBar 
          league={selectedLeague}
          key={selectedLeague}
          filters={filters}
          onFilterChange={setFilters}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          filterMode={filterMode}
          onFilterModeChange={setFilterMode}
        />

        <div className="picks-grid">
          {filteredPicks.map(({ pick, game }, index) => (
            <PickCard 
              key={`${index}`} 
              pick={pick} 
              game={game}
              league={pick.league || game.league || selectedLeague}
              onAddToParlay={(pick, game) => {
                // Check if pick already exists in parlay
                const exists = parlayPicks.some(p => 
                  p.pick.player === pick.player && 
                  p.pick.prop === pick.prop &&
                  p.game === game.game
                );
                if (!exists) {
                  setParlayPicks([...parlayPicks, { pick, game, selection: null }]);
                }
              }}
              isInParlay={parlayPicks.some(p => 
                p.pick.player === pick.player && 
                p.pick.prop === pick.prop &&
                p.game === game.game
              )}
              onCardClick={(pick, game) => {
                setSelectedPlayer(pick);
                setSelectedGame(game);
                setIsModalOpen(true);
              }}
            />
          ))}
        </div>

        {/* Player Stats Modal */}
        <PlayerStatsModal
          pick={selectedPlayer}
          game={selectedGame}
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedPlayer(null);
            setSelectedGame(null);
          }}
        />

        {games.length === 0 && (
          <div className="error">
            <h2>📁 No Games Found</h2>
            <p>No JSON files were loaded. Please make sure the NBA picks JSON files are in the public folder.</p>
          </div>
        )}

        {games.length > 0 && filteredPicks.length === 0 && (
          <div className="error">
            <h2>🔍 No Picks Match Filters</h2>
            <p>Try adjusting your filters or search query.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;
